import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as AssistantIcon,
  Autorenew as AutorenewIcon,
  Computer as ComputerIcon,
  ContentCopy as ContentCopyIcon,
  Block as BlockIcon,
  DeleteOutline as DeleteOutlineIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  PhoneForwarded as PhoneForwardedIcon,
  PhoneInTalk as PhoneInTalkIcon,
  SettingsEthernet as SettingsEthernetIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import {
  canManageTelephony,
  connectorSystemKey,
  createTelephonyPairingCode,
  deleteTelephonyConnector,
  disableTelephonyConnector,
  isUrlConnector,
  rotateTelephonyUrlEndpoint,
  setTelephonyConnectorRole,
  subscribeToTelephonyConnectors,
  TELEPHONY_SYSTEMS,
  toDate
} from '../../services/telephonyService';
import { detectTelephonyConnectorDownload } from '../../utils/telephonyConnectorPlatform';
import TelephonyUrlSetupDialog from './TelephonyUrlSetupDialog';

// URL-Anschlüsse haben keinen Heartbeat; Lebenszeichen sind nur Anrufe. Bleiben sie
// länger aus, ist meist die Skin gewechselt oder der Client neu installiert worden
// (Konzept 4.3, Vorbehalt 8). Bewusst fester Wert statt Einstellung.
const URL_CONNECTOR_SILENCE_WARNING_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

const formatSince = (date) => {
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `vor ${hours} Std.`;
  return `vor ${Math.round(hours / 24)} Tagen`;
};

const urlConnectorStatus = (connector) => {
  if (connector?.enabled === false) return { label: 'Deaktiviert', color: 'default' };
  const lastEventAt = toDate(connector?.lastEventAt);
  if (!lastEventAt) return { label: 'Wartet auf ersten Anruf', color: 'warning' };
  const silentDays = (Date.now() - lastEventAt.getTime()) / DAY_MS;
  if (silentDays >= URL_CONNECTOR_SILENCE_WARNING_DAYS) {
    return { label: `Keine Meldung seit ${Math.floor(silentDays)} Tagen`, color: 'error', silent: true };
  }
  return { label: `Letzter Anruf ${formatSince(lastEventAt)}`, color: 'success' };
};

const connectorStatus = (connector) => {
  if (isUrlConnector(connector)) return urlConnectorStatus(connector);
  if (connector?.enabled === false) return { label: 'Deaktiviert', color: 'default' };
  const lastSeenAt = toDate(connector?.lastSeenAt);
  if (!lastSeenAt) return { label: 'Noch nicht verbunden', color: 'warning' };
  if (Date.now() - lastSeenAt.getTime() <= 2 * 60 * 1000) {
    return { label: 'Online', color: 'success' };
  }
  return { label: 'Offline', color: 'error' };
};

const TelephonyConnectorSettingsSection = () => {
  const user = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pairingOpen, setPairingOpen] = useState(false);
  const [displayName, setDisplayName] = useState('Dispositions-PC');
  const [pairing, setPairing] = useState(null);
  const [creatingCode, setCreatingCode] = useState(false);
  const [disablingConnectorId, setDisablingConnectorId] = useState('');
  const [deletingConnectorId, setDeletingConnectorId] = useState('');
  const [updatingRoleConnectorId, setUpdatingRoleConnectorId] = useState('');
  const [chooserOpen, setChooserOpen] = useState(false);
  const [urlSetup, setUrlSetup] = useState(null); // { provider, rotated? }
  const [rotatingConnectorId, setRotatingConnectorId] = useState('');

  const mayManage = canManageTelephony(user);
  const activeConnectors = useMemo(
    () => connectors.filter((connector) => connector.enabled !== false),
    [connectors]
  );
  const hasFritzConnectors = useMemo(
    () => connectors.some((connector) => !isUrlConnector(connector)),
    [connectors]
  );
  const silentUrlConnectors = useMemo(
    () => activeConnectors.filter((connector) => isUrlConnector(connector) && connectorStatus(connector).silent),
    [activeConnectors]
  );
  // Anschlüsse nach Anlage gruppiert, in der Reihenfolge von TELEPHONY_SYSTEMS.
  const connectorGroups = useMemo(
    () => Object.keys(TELEPHONY_SYSTEMS)
      .map((key) => ({
        key,
        items: connectors.filter((connector) => connectorSystemKey(connector) === key)
      }))
      .filter((group) => group.items.length > 0),
    [connectors]
  );
  const systemsInUse = useMemo(
    () => new Set(activeConnectors.map((connector) => connectorSystemKey(connector))),
    [activeConnectors]
  );
  const connectorDownload = useMemo(() => detectTelephonyConnectorDownload(), []);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToTelephonyConnectors(user, (items) => {
      setConnectors(items);
      setLoading(false);
      setError('');
    }, (snapshotError) => {
      setLoading(false);
      setError(snapshotError.message || 'Connector-Status konnte nicht geladen werden.');
    });
    return unsubscribe;
  }, [user]);

  const handleCreatePairingCode = async () => {
    setCreatingCode(true);
    setError('');
    try {
      const result = await createTelephonyPairingCode(displayName);
      setPairing(result);
    } catch (callError) {
      setError(callError.message || 'Pairing-Code konnte nicht erzeugt werden.');
    } finally {
      setCreatingCode(false);
    }
  };

  const handleCopyCode = async () => {
    if (pairing?.code) await navigator.clipboard.writeText(pairing.code);
  };

  const closePairing = () => {
    setPairingOpen(false);
    setPairing(null);
  };

  const handleDisable = async (connector) => {
    if (!connector || !window.confirm(`${connector.displayName || connector.hostname || 'Diesen Connector'} wirklich deaktivieren?`)) return;
    setDisablingConnectorId(connector.id);
    setError('');
    try {
      await disableTelephonyConnector(connector.id);
    } catch (callError) {
      setError(callError.message || 'Connector konnte nicht deaktiviert werden.');
    } finally {
      setDisablingConnectorId('');
    }
  };

  const handleRoleChange = async (connector, role) => {
    if (!connector || connector.role === role) return;
    setUpdatingRoleConnectorId(connector.id);
    setError('');
    try {
      await setTelephonyConnectorRole(connector.id, role);
    } catch (callError) {
      setError(callError.message || 'Connector-Rolle konnte nicht aktualisiert werden.');
    } finally {
      setUpdatingRoleConnectorId('');
    }
  };

  const handleChooseSystem = (key) => {
    setChooserOpen(false);
    if (key === 'fritzbox') {
      // Der FRITZ!Box-Assistent führt durch Download, Installation und Kopplung.
      navigate('/einstellungen/telefonie-anleitung');
      return;
    }
    setUrlSetup({ provider: key, rotated: null });
  };

  const handleRotate = async (connector) => {
    if (!connector || !window.confirm(
      `Neue Adressen für ${connector.displayName || 'diesen Platz'} erzeugen? Die bisherigen gelten sofort nicht mehr und müssen in der Anlage ersetzt werden.`
    )) return;
    setRotatingConnectorId(connector.id);
    setError('');
    try {
      const result = await rotateTelephonyUrlEndpoint(connector.id);
      setUrlSetup({ provider: connectorSystemKey(connector), rotated: result });
    } catch (callError) {
      setError(callError.message || 'Adressen konnten nicht neu erzeugt werden.');
    } finally {
      setRotatingConnectorId('');
    }
  };

  const handleDelete = async (connector) => {
    const connectorName = connector?.displayName || connector?.hostname || 'Diesen Connector';
    if (!connector || connector.enabled !== false || !window.confirm(
      `${connectorName} dauerhaft aus dem Portal entfernen? Diese Aktion kann nicht rückgängig gemacht werden.`
    )) return;
    setDeletingConnectorId(connector.id);
    setError('');
    try {
      await deleteTelephonyConnector(connector.id);
    } catch (callError) {
      setError(callError.message || 'Connector konnte nicht entfernt werden.');
    } finally {
      setDeletingConnectorId('');
    }
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PhoneInTalkIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Telefonie und Anruferkennung
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Anschlüsse der Telefonanlage für eingehende Anrufe
            </Typography>
          </Box>
        </Stack>
      </Box>

      <List disablePadding>
        <ListItem>
          <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsEthernetIcon color={connectors.length ? 'primary' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary={`${activeConnectors.length} aktive Anschlüsse`}
                  secondary={connectors.length
                    ? `${connectors.length - activeConnectors.length} deaktiviert`
                    : 'Noch keine Telefonanlage angebunden'}
                />
              </Stack>
              {loading && <CircularProgress size={20} />}
            </Stack>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Melden mehrere Computer oder Plätze denselben Anruf, werden die Meldungen
              zusammengeführt, sodass im Portal nur ein Hinweis erscheint.
            </Typography>

            {silentUrlConnectors.length > 0 && (
              <Alert severity="warning">
                Von {silentUrlConnectors.map((connector) => connector.displayName || 'einem Platz').join(', ')}
                {' '}kam seit mindestens {URL_CONNECTOR_SILENCE_WARNING_DAYS} Tagen kein Anruf. Wurde
                die Skin gewechselt oder der Client neu installiert? Dann fehlen dort die Adressen –
                bei Bedarf über „Adressen neu erzeugen“ neu eintragen.
              </Alert>
            )}

            {connectorGroups.map((group) => (
              <Stack key={group.key} spacing={1.5}>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {TELEPHONY_SYSTEMS[group.key].label}
                </Typography>
                {group.items.map((connector) => {
                  const itemStatus = connectorStatus(connector);
                  const role = connector.role || 'standard';
                  const isUpdatingRole = updatingRoleConnectorId === connector.id;
                  const isDisabling = disablingConnectorId === connector.id;
                  const isDeleting = deletingConnectorId === connector.id;
                  const isRotating = rotatingConnectorId === connector.id;
                  const urlConnector = isUrlConnector(connector);
                  return (
                    <Box
                      key={connector.id}
                      sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {urlConnector
                            ? <PhoneForwardedIcon color={connector.enabled === false ? 'disabled' : 'primary'} />
                            : <ComputerIcon color={connector.enabled === false ? 'disabled' : 'primary'} />}
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {connector.displayName || connector.hostname || 'Unbenannter Anschluss'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {urlConnector
                                ? `${connector.extension ? `Durchwahl ${connector.extension}` : 'Ohne Durchwahl'} | ${connector.reportsConnect ? 'meldet Annahme' : 'Annahme wird nicht gemeldet'}`
                                : `${connector.hostname || 'Unbekannter Computer'} | ${connector.platform || 'System unbekannt'} | Version ${connector.softwareVersion || 'unbekannt'}`}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems="center">
                          <Chip size="small" label={itemStatus.label} color={itemStatus.color} variant="outlined" />
                          {!urlConnector && (
                            <Chip
                              size="small"
                              label={role === 'primary' ? 'Hauptgerät' : role === 'backup' ? 'Ersatzgerät' : 'Standard'}
                              color={role === 'primary' ? 'primary' : role === 'backup' ? 'warning' : 'default'}
                            />
                          )}
                          {mayManage && urlConnector && connector.enabled !== false && (
                            <Button
                              size="small"
                              disabled={isRotating}
                              startIcon={isRotating ? <CircularProgress size={16} /> : <AutorenewIcon />}
                              onClick={() => handleRotate(connector)}
                            >
                              Adressen neu erzeugen
                            </Button>
                          )}
                          {mayManage && connector.enabled !== false && (
                            <>
                              {!urlConnector && (
                                <>
                              <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'primary')}>
                                Hauptgerät
                              </Button>
                              <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'backup')}>
                                Ersatzgerät
                              </Button>
                              {role !== 'standard' && (
                                <Button size="small" disabled={isUpdatingRole} onClick={() => handleRoleChange(connector, 'standard')}>
                                  Standard
                                </Button>
                              )}
                                </>
                              )}
                              <Button
                                size="small"
                                color="error"
                                disabled={isDisabling}
                                startIcon={isDisabling ? <CircularProgress size={16} /> : <BlockIcon />}
                                onClick={() => handleDisable(connector)}
                              >
                                Deaktivieren
                              </Button>
                            </>
                          )}
                          {mayManage && connector.enabled === false && (
                            <Button
                              size="small"
                              color="error"
                              disabled={isDeleting}
                              startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
                              onClick={() => handleDelete(connector)}
                            >
                              Entfernen
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            ))}

            {hasFritzConnectors && (
              <Alert severity="info">
                FRITZ!Box: Ein Hauptgerät wird als bevorzugte Quelle markiert. Ersatzgeräte bleiben
                empfangsbereit und übernehmen automatisch, wenn das Hauptgerät ausfällt.
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {mayManage && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setChooserOpen(true)}
                >
                  Anschluss einrichten
                </Button>
              )}
              {hasFritzConnectors && (connectorDownload ? (
                <Button
                  component="a"
                  href={connectorDownload.href}
                  download
                  variant="contained"
                  startIcon={<DownloadIcon />}
                >
                  {connectorDownload.label}
                </Button>
              ) : (
                <Button variant="outlined" disabled startIcon={<DownloadIcon />}>
                  Kein Installer für dieses System
                </Button>
              ))}
              {hasFritzConnectors && (
                <Button
                  component={RouterLink}
                  to="/einstellungen/telefonie-anleitung"
                  variant="outlined"
                  startIcon={<AssistantIcon />}
                >
                  FRITZ!Box-Assistent
                </Button>
              )}
              {mayManage && hasFritzConnectors && (
                <Button
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => setPairingOpen(true)}
                >
                  Connector koppeln
                </Button>
              )}
            </Stack>

            {!mayManage && (
              <Alert severity="info">
                Nur Administratoren können Telefonanlagen anbinden. Status und Anleitung
                bleiben für die Disposition sichtbar.
              </Alert>
            )}
            {hasFritzConnectors && connectorDownload?.system && (
              <Typography variant="caption" color="text.secondary">
                Erkanntes System: {connectorDownload.system}
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </ListItem>
      </List>

      <Dialog open={pairingOpen} onClose={closePairing} maxWidth="sm" fullWidth>
        <DialogTitle>Telefonie-Connector koppeln</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              {!connectorDownload
                ? 'Die Einrichtung muss auf einem unterstützten Windows-, macOS- oder Linux-Computer durchgeführt werden.'
                : connectorDownload.key === 'windows'
                  ? 'Den Code innerhalb von zehn Minuten im Windows-Setup-Assistenten eingeben.'
                  : connectorDownload.key === 'macos'
                    ? 'Den Code innerhalb von zehn Minuten in der installierten NextCab-App eingeben.'
                  : 'Der Code ist zehn Minuten und nur einmal gültig. Nach der Paketinstallation wird er mit dem unten angezeigten Befehl eingegeben.'}
            </Alert>
            {connectorDownload?.key === 'macos' && (
              <Alert severity="warning">
                Der aktuelle macOS-Testinstaller ist noch nicht von Apple signiert und
                notarisiert. Falls macOS ihn blockiert, versuchen Sie einmal, das Paket zu
                öffnen. Wählen Sie danach unter
                <strong> Systemeinstellungen &gt; Datenschutz &amp; Sicherheit &gt; Dennoch öffnen</strong>
                {' '}und bestätigen Sie mit Mac-Passwort oder Touch ID.
              </Alert>
            )}
            <TextField
              label="Name des Computers"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              disabled={Boolean(pairing)}
              fullWidth
            />
            {!pairing ? (
              <Button
                variant="contained"
                onClick={handleCreatePairingCode}
                disabled={creatingCode || !displayName.trim()}
                startIcon={creatingCode ? <CircularProgress size={18} color="inherit" /> : <ComputerIcon />}
              >
                Pairing-Code erzeugen
              </Button>
            ) : (
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Pairing-Code
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h4" fontFamily="monospace" letterSpacing={3}>
                    {pairing.code}
                  </Typography>
                  <Button onClick={handleCopyCode} startIcon={<ContentCopyIcon />}>
                    Kopieren
                  </Button>
                </Stack>
                {!connectorDownload ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Einstellungen auf dem Dispositionscomputer öffnen und dort einen
                    neuen Pairing-Code erzeugen.
                  </Typography>
                ) : connectorDownload.key === 'windows' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Diesen Code jetzt in den Windows-Setup-Assistenten übernehmen.
                  </Typography>
                ) : connectorDownload.key === 'macos' ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Diesen Code in die Anwendung <strong>NextCab Telefonie-Connector</strong>
                    {' '}eingeben. Die Anwendung wird nach der Paketinstallation automatisch
                    geöffnet und zeigt den Verbindungsstatus auf der letzten Seite.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Nach Installation des Linux-Pakets ausführen:
                    </Typography>
                    <Box component="pre" sx={{ p: 1, bgcolor: 'grey.900', color: 'grey.100', overflowX: 'auto' }}>
                      {`nextcab-telephony-connector setup --code ${pairing.code}`}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePairing}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={chooserOpen} onClose={() => setChooserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Welche Telefonanlage nutzen Sie?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {Object.entries(TELEPHONY_SYSTEMS).map(([key, system]) => (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => handleChooseSystem(key)}
                sx={{
                  p: 2,
                  textAlign: 'left',
                  cursor: 'pointer',
                  border: 1,
                  borderColor: systemsInUse.has(key) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  font: 'inherit',
                  '&:hover, &:focus-visible': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">{system.label}</Typography>
                  {systemsInUse.has(key) && <Chip size="small" color="primary" label="Bereits im Einsatz" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">{system.description}</Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary">
              Ihre Anlage ist nicht dabei und kann auch keine Web-Adresse aufrufen? Wenden Sie sich
              an den Support, damit die Anbindung geprüft werden kann.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChooserOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      <TelephonyUrlSetupDialog
        open={Boolean(urlSetup)}
        onClose={() => setUrlSetup(null)}
        provider={urlSetup?.provider}
        rotated={urlSetup?.rotated || null}
        connectors={connectors}
      />
    </>
  );
};

export default TelephonyConnectorSettingsSection;
